import { TextNode, EditorConfig, LexicalNode, NodeKey, SerializedTextNode, Spread } from "lexical";

export type SpeakerPayload = {
    sourceIndex: number;      // Index into ScriptLayerClip.names/colors
    speakerId?: string | number;  // Speaker ID from diarization - ElevenLabs returns strings like "speaker_0"
    firestoreSpeakerId?: string; // Optional speaker entity ID for project-level speakers
    displayName: string;      // The rendered speaker name
    color: string;            // Color matching the waveform
    clipId: string;           // The script layer clip ID
    overrideId?: string;      // If from a speaker override, the override ID (for deletion)
    segmentStartSec?: number; // Start time of this speaker segment (for deletion)
    segmentEndSec?: number;   // End time of this speaker segment (for deletion)
    isMuted?: boolean;        // True when this speaker has active audio mute ranges
};

export type SerializedSpeakerNode = Spread<
    {
        type: 'speaker';
        version: 1;
        payload: SpeakerPayload;
    },
    SerializedTextNode
>;

/**
 * SpeakerNode - A styled text node for speaker labels.
 * Displays the speaker name with color matching their waveform.
 * Does not have timestamp like WordNode - it's just a label.
 */
export class SpeakerNode extends TextNode {
    __payload: SpeakerPayload;

    static getType(): string {
        return "speaker";
    }

    static clone(node: SpeakerNode): SpeakerNode {
        return new SpeakerNode(node.__payload.displayName, { ...node.__payload }, node.__key);
    }

    constructor(text: string, payload: SpeakerPayload, key?: NodeKey) {
        super(text, key);
        this.__payload = payload;
    }

    createDOM(config: EditorConfig): HTMLElement {
        // Create a container span for the speaker label
        const container = document.createElement('span');
        container.style.fontFamily = 'Inter, sans-serif';
        container.style.fontSize = '13px';
        container.style.fontWeight = '600';
        container.style.color = this.__payload.color;
        container.style.display = 'inline-flex';
        container.style.alignItems = 'center';
        container.style.gap = '0px';
        container.style.marginTop = '16px';
        container.style.marginBottom = '4px';
        container.style.userSelect = 'none';
        container.style.cursor = 'pointer';
        container.style.padding = '2px 4px';
        container.style.borderRadius = '4px';
        container.style.transition = 'background-color 0.15s ease';
        container.classList.add('speaker-label');
        
        // Check if this is a pending speaker (empty displayName)
        const isPending = !this.__payload.displayName;
        const isMuted = Boolean(this.__payload.isMuted);
        if (isMuted) {
            container.style.opacity = '0.58';
        }
        
        // Add the text content
        const textSpan = document.createElement('span');
        textSpan.textContent = this.__text;
        textSpan.classList.add('speaker-text');
        if (isPending) {
            // Style pending speakers differently - dashed border to indicate editable
            textSpan.style.fontStyle = 'italic';
            textSpan.style.opacity = '0.7';
            container.style.border = `1px dashed ${this.__payload.color}`;
            container.style.backgroundColor = `${this.__payload.color}10`;
            container.classList.add('speaker-label-pending');
        }
        container.appendChild(textSpan);
        
        // Add the delete button for ALL speaker labels (shows on hover)
        const deleteBtn = document.createElement('span');
        deleteBtn.innerHTML = '×';
        deleteBtn.style.fontSize = '16px';
        deleteBtn.style.fontWeight = 'normal';
        deleteBtn.style.color = '#9CA3AF';
        deleteBtn.style.cursor = 'pointer';
        deleteBtn.style.opacity = '0';
        deleteBtn.style.transition = 'opacity 0.15s ease, color 0.15s ease';
        deleteBtn.style.marginLeft = '6px';
        deleteBtn.style.lineHeight = '1';
        deleteBtn.style.padding = '0 2px';
        deleteBtn.classList.add('speaker-delete-btn');
        if (this.__payload.overrideId) {
            deleteBtn.dataset.overrideId = this.__payload.overrideId;
        }
        deleteBtn.dataset.isPending = isPending ? 'true' : 'false';
        deleteBtn.dataset.sourceIndex = String(this.__payload.sourceIndex);
        deleteBtn.dataset.clipId = this.__payload.clipId;
        if (this.__payload.firestoreSpeakerId) {
            deleteBtn.dataset.firestoreSpeakerId = this.__payload.firestoreSpeakerId;
        }
        // Store diarization speaker ID for proper override matching
        if (this.__payload.speakerId != null) {
            deleteBtn.dataset.diarizationSpeakerId = String(this.__payload.speakerId);
        }
        // Store segment time range for deletion
        if (this.__payload.segmentStartSec !== undefined) {
            deleteBtn.dataset.segmentStartSec = String(this.__payload.segmentStartSec);
        }
        if (this.__payload.segmentEndSec !== undefined) {
            deleteBtn.dataset.segmentEndSec = String(this.__payload.segmentEndSec);
        }
        
        // Show on hover
        container.addEventListener('mouseenter', () => {
            deleteBtn.style.opacity = '1';
            container.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
        });
        container.addEventListener('mouseleave', () => {
            deleteBtn.style.opacity = '0';
            container.style.backgroundColor = isPending ? `${this.__payload.color}10` : 'transparent';
        });
        
        // Delete button hover effect
        deleteBtn.addEventListener('mouseenter', () => {
            deleteBtn.style.color = '#EF4444';
        });
        deleteBtn.addEventListener('mouseleave', () => {
            deleteBtn.style.color = '#9CA3AF';
        });
        
        container.appendChild(deleteBtn);
        
        // Prevent default Lexical selection behavior on mousedown which can cause scroll
        container.addEventListener('mousedown', (e) => {
            e.preventDefault();
        });
        
        return container;
    }

    updateDOM(prevNode: SpeakerNode, dom: HTMLElement, config: EditorConfig): boolean {
        const updated = super.updateDOM(prevNode, dom, config);
        // Update color if it changed
        if (prevNode.__payload.color !== this.__payload.color) {
            dom.style.color = this.__payload.color;
        }
        if (prevNode.__payload.isMuted !== this.__payload.isMuted) {
            dom.style.opacity = this.__payload.isMuted ? '0.58' : '1';
        }
        return updated;
    }

    static importJSON(serializedNode: SerializedSpeakerNode): SpeakerNode {
        const node = new SpeakerNode(
            serializedNode.text,
            serializedNode.payload
        );
        node.setFormat(serializedNode.format);
        node.setDetail(serializedNode.detail);
        node.setMode(serializedNode.mode);
        node.setStyle(serializedNode.style);
        return node;
    }

    exportJSON(): SerializedSpeakerNode {
        return {
            ...super.exportJSON(),
            type: 'speaker',
            version: 1,
            payload: this.__payload,
        };
    }

    // Allow getting/setting the display name
    getDisplayName(): string {
        return this.__payload.displayName;
    }

    setDisplayName(name: string): void {
        const self = this.getWritable();
        self.__payload = { ...self.__payload, displayName: name };
        self.setTextContent(name + ':');
    }

    getSourceIndex(): number {
        return this.__payload.sourceIndex;
    }

    getSpeakerId(): string | number | undefined {
        return this.__payload.speakerId;
    }

    getColor(): string {
        return this.__payload.color;
    }

    getClipId(): string {
        return this.__payload.clipId;
    }
}

export function $createSpeakerNode(payload: SpeakerPayload): SpeakerNode {
    // Text content is the display name with colon, or placeholder if empty (pending)
    const displayText = payload.displayName 
        ? `${payload.displayName}` 
        : 'New Speaker';
    return new SpeakerNode(displayText, payload);
}

export function $isSpeakerNode(node?: LexicalNode | null): node is SpeakerNode {
    return node instanceof SpeakerNode;
}
